// "Create Your Own Combo" — pick a tier (Combo/Super/Mega/Ultra), then select
// exactly that many admin-eligible products with a pack size each. Different
// pickles cost different amounts to make, so there's no flat tier price —
// each eligible product has its own admin-set price (comboSettings.tiers.*
// .productPrices), and the combo total is the sum of whichever ones the
// customer picks. Adds as ONE cart line (create_checkout_order's combo
// branch in migration 030 re-sums and re-validates everything server-side
// before trusting it).
import { useState } from "react";
import { Package, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatPrice } from "@/lib/cart";
import { track } from "@/lib/analytics";

const TIER_ORDER = ["combo", "super_combo", "mega_combo", "ultra_combo"];

export const ComboBuilderModal = ({ open, onClose, onAdded, comboSettings, products, cart }) => {
  const [tierKey, setTierKey] = useState("combo");
  const [selections, setSelections] = useState([]); // { product_id, product_name, size_label }

  const tier = comboSettings.tiers[tierKey];
  const packSizes = comboSettings.packSizes;
  const eligibleProducts = (products || []).filter((p) => p.id in tier.productPrices);
  const totalPrice = selections.reduce((sum, s) => sum + (Number(tier.productPrices[s.product_id]) || 0), 0);

  const changeTier = (key) => {
    setTierKey(key);
    setSelections([]);
  };

  const isSelected = (productId) => selections.some((s) => s.product_id === productId);

  const toggleProduct = (product) => {
    if (isSelected(product.id)) {
      setSelections((prev) => prev.filter((s) => s.product_id !== product.id));
      track("combo_product_deselected", { combo_type: tierKey, product_id: product.id });
      return;
    }
    if (selections.length >= tier.itemCount) {
      toast.error(`You can only choose ${tier.itemCount} products for ${tier.label} — remove one first`);
      return;
    }
    setSelections((prev) => [...prev, { product_id: product.id, product_name: product.name, size_label: packSizes[0] }]);
    track("combo_product_selected", { combo_type: tierKey, product_id: product.id, size_label: packSizes[0], price: tier.productPrices[product.id] });
  };

  const changeSize = (productId, sizeLabel) => {
    setSelections((prev) => prev.map((s) => (s.product_id === productId ? { ...s, size_label: sizeLabel } : s)));
    track("combo_product_selected", { combo_type: tierKey, product_id: productId, size_label: sizeLabel, price: tier.productPrices[productId] });
  };

  const canAdd = selections.length === tier.itemCount;

  const handleAdd = () => {
    if (!canAdd) return;
    cart.addComboItem(tier, tierKey, selections, totalPrice);
    track("combo_added_to_cart", {
      combo_type: tierKey,
      tier_label: tier.label,
      price: totalPrice,
      product_ids: selections.map((s) => s.product_id),
      pack_sizes: selections.map((s) => s.size_label),
    });
    toast.success(`${tier.label} added to cart`, { description: formatPrice(totalPrice) });
    setSelections([]);
    onClose();
    onAdded?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="combo-builder-modal"
        className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm max-w-lg w-[calc(100%-2rem)] max-h-[85vh] overflow-y-auto"
      >
        <DialogTitle className="flex items-center gap-2 font-display text-xl text-[#5A2D1D]">
          <span className="inline-flex p-1.5 rounded-sm bg-[#556B2F]/10 text-[#556B2F]">
            <Package className="w-4 h-4" />
          </span>
          Create Your Own Combo
        </DialogTitle>

        <div className="flex flex-wrap gap-2 mt-2" data-testid="combo-tier-selector">
          {TIER_ORDER.map((key) => {
            const t = comboSettings.tiers[key];
            const active = tierKey === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`combo-tier-${key}`}
                onClick={() => changeTier(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  active ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]" : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
                }`}
              >
                {t.label} · {t.itemCount} items
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-3">
          <span
            data-testid="combo-selection-count"
            className={`text-sm font-bold ${canAdd ? "text-[#556B2F]" : "text-[#2E1A12]/70"}`}
          >
            {selections.length} / {tier.itemCount} selected
          </span>
          <span data-testid="combo-running-total" className="font-display text-lg font-bold text-[#5A2D1D]">
            {formatPrice(totalPrice)}
          </span>
        </div>

        {eligibleProducts.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#2E1A12]/60">
            No products are configured for {tier.label} yet.
          </div>
        ) : (
          <div className="mt-2 space-y-2 max-h-[40vh] overflow-y-auto pr-1" data-testid="combo-product-list">
            {eligibleProducts.map((p) => {
              const sel = selections.find((s) => s.product_id === p.id);
              const checked = !!sel;
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 p-2.5 rounded-sm border ${
                    checked ? "border-[#556B2F] bg-[#556B2F]/5" : "border-[#5A2D1D]/15 bg-white/40"
                  }`}
                >
                  <button
                    type="button"
                    data-testid={`combo-product-checkbox-${p.slug}`}
                    onClick={() => toggleProduct(p)}
                    aria-pressed={checked}
                    className={`flex-shrink-0 w-5 h-5 rounded-sm border-2 grid place-items-center transition-colors ${
                      checked ? "bg-[#556B2F] border-[#556B2F]" : "border-[#5A2D1D]/40"
                    }`}
                  >
                    {checked && <Check className="w-3.5 h-3.5 text-[#F5E6CC]" />}
                  </button>
                  <img src={p.image_url} alt={p.name} className="w-10 h-10 rounded-sm object-cover flex-shrink-0" />
                  <span className="flex-1 text-sm font-semibold text-[#2E1A12] truncate">{p.name}</span>
                  <span className="text-xs font-bold text-[#556B2F] flex-shrink-0">{formatPrice(tier.productPrices[p.id])}</span>
                  <Select
                    value={sel?.size_label || packSizes[0]}
                    onValueChange={(v) => changeSize(p.id, v)}
                    disabled={!checked}
                  >
                    <SelectTrigger
                      data-testid={`combo-size-${p.slug}`}
                      className="w-24 h-8 rounded-sm border-[#5A2D1D]/30 bg-[#F5E6CC] text-xs text-[#2E1A12] flex-shrink-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {packSizes.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          data-testid="combo-add-to-cart"
          disabled={!canAdd}
          onClick={handleAdd}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/30 disabled:cursor-not-allowed text-[#F5E6CC] py-3 rounded-sm font-bold transition-colors"
        >
          {canAdd ? `Add to Cart — ${formatPrice(totalPrice)}` : `Select ${tier.itemCount - selections.length} more to continue`}
        </button>
      </DialogContent>
    </Dialog>
  );
};
